import * as React from 'react';
import {
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
  RefreshCw,
  Loader2,
} from 'lucide-react';
import { Formik } from 'formik';
import { PageHeader } from '../../components/page-header';
import { Button } from '../../components/ui/button';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import {
  getBearerToken,
  getCurrentUser,
} from '../../../application/common/utils/authUtils';
import { NewSelectCircleOrPerson } from '../../../application/common/PickerSearch';
import { cn } from '../../lib/utils';

// ─── Types ───────────────────────────────────────────────────────────────────

interface UserActivityKpis {
  notInterested: { id: string };
  knocks: { id: string };
  callBack: { id: string };
  interested: { id: string };
  gavePitch: { id: string };
  sales: { id: string };
  notHome: { id: string };
  [key: string]: { id: string };
}

declare const global: {
  me: {
    id: string;
    orgID: string;
    isAdmin: boolean;
    functionalRoles: string[];
    myManagerCircles: string[];
    myCircles: string[];
    imageName?: string;
    name: string;
  };
  appSettings: {
    userActivityKpis?: UserActivityKpis;
  };
};

interface ActivityItem {
  id: string;
  name: string;
  team: string;
  firstContactAfter9AM: string | null;
  thirdContactAfter9AM: string | null;
  lastBefore3pm: string | null;
  firstAfter4pm: string | null;
  thirdToLastPin: string | null;
  lastPin: string | null;
  pinsDropped: number | string;
  sales: number | string | null;
  notHomeCount: number | string | null;
  notInterestedCount: number | string | null;
  gavePitchCount: number | string | null;
  callBacksCount: number | string | null;
  percentNotInterested: number | string | null;
  percentGavePitch: number | string | null;
  percentNotHome: number | string | null;
  percentCallBack: number | string | null;
  knocksToSales: number | string | null;
  notInterestedToSales: number | string | null;
  gavePitchToSales: number | string | null;
}

interface ApiTeamMetric {
  name: string;
  user_id: string;
  teams: {
    circle_id: string;
    circle_name: string;
    level_id: string;
    level_name: string;
    is_direct_member: boolean;
  }[];
  sales: number | string | null;
  first_pin_after_9am: string | null;
  third_pin_after_9am: string | null;
  last_before_3pm: string | null;
  first_after_4pm: string | null;
  third_to_last_pin: string | null;
  last_pin: string | null;
  pins_dropped: number | string;
  not_home_count: number | string | null;
  not_interested_count: number | string | null;
  gave_pitch_count: number | string | null;
  call_backs_count: number | string | null;
  percent_not_interested: number | string | null;
  percent_gave_pitch: number | string | null;
  percent_not_home: number | string | null;
  percent_call_back: number | string | null;
  knocks_to_sales: number | string | null;
  not_interested_to_sales: number | string | null;
  gave_pitch_to_sales: number | string | null;
}

type Order = 'asc' | 'desc';
type SortableKey = keyof ActivityItem;

// ─── Helpers ─────────────────────────────────────────────────────────────────

function getCurrentLocalDate(): string {
  return new Date().toLocaleDateString('en-CA');
}

function getUserActivityKpis(): UserActivityKpis | undefined {
  try {
    // @ts-ignore
    if (!global.appSettings?.userActivityKpis) {
      return undefined;
    }
    // @ts-ignore
    return global.appSettings.userActivityKpis;
  } catch {
    return undefined;
  }
}

function formatDisplayValue(value: number | string | null): string | number {
  if (value === null || value === 0 || value === '0') {
    return '--';
  }
  return value;
}

function formatPercent(value: number | string | null): string {
  if (value === null || value === '--') {
    return '--';
  }
  return `${value}%`;
}

function getSortableValue(value: any): string | number {
  if (value === null || value === undefined || value === '--') {
    return -Infinity;
  }
  if (typeof value === 'string' && value.endsWith('%')) {
    return parseFloat(value.replace('%', ''));
  }
  if (
    typeof value === 'string' &&
    /\d{1,2}:\d{2}(:\d{2})?\s*(AM|PM)?/i.test(value)
  ) {
    try {
      const today = new Date().toLocaleDateString('en-CA');
      return new Date(`${today} ${value}`).getTime();
    } catch {
      return value;
    }
  }
  if (typeof value === 'string' && !isNaN(Number(value))) {
    return Number(value);
  }
  return value;
}

function sortData(
  data: ActivityItem[],
  orderBy: SortableKey,
  order: Order
): ActivityItem[] {
  return [...data].sort((a, b) => {
    const valueA = getSortableValue(a[orderBy]);
    const valueB = getSortableValue(b[orderBy]);

    if (valueA === -Infinity && valueB !== -Infinity) {
      return 1;
    }
    if (valueA !== -Infinity && valueB === -Infinity) {
      return -1;
    }
    if (valueA === -Infinity && valueB === -Infinity) {
      return 0;
    }

    if (valueA < valueB) {
      return order === 'asc' ? -1 : 1;
    }
    if (valueA > valueB) {
      return order === 'asc' ? 1 : -1;
    }
    return 0;
  });
}

// ─── Column definitions ──────────────────────────────────────────────────────

interface ColumnDef {
  key: SortableKey;
  label: string;
  kpiOnly?: boolean;
  format?: (value: any) => string;
}

const BASE_COLUMNS: ColumnDef[] = [
  { key: 'name', label: 'Name' },
  { key: 'team', label: 'Team' },
  { key: 'firstContactAfter9AM', label: 'First After 9am' },
  { key: 'thirdContactAfter9AM', label: 'Third After 9am' },
  { key: 'lastBefore3pm', label: 'Last Before 3pm' },
  { key: 'firstAfter4pm', label: 'First After 4pm' },
  { key: 'thirdToLastPin', label: 'Third To Last Pin' },
  { key: 'lastPin', label: 'Last Pin' },
  { key: 'pinsDropped', label: 'Pins Dropped' },
];

const KPI_COLUMNS: ColumnDef[] = [
  { key: 'sales', label: 'Sales', kpiOnly: true },
  { key: 'notHomeCount', label: 'Not Home', kpiOnly: true },
  { key: 'notInterestedCount', label: 'Not Interested', kpiOnly: true },
  { key: 'gavePitchCount', label: 'Gave Pitch', kpiOnly: true },
  { key: 'callBacksCount', label: 'Call Backs', kpiOnly: true },
  {
    key: 'percentNotInterested',
    label: '% Not Interested',
    kpiOnly: true,
    format: formatPercent,
  },
  {
    key: 'percentGavePitch',
    label: '% Gave Pitch',
    kpiOnly: true,
    format: formatPercent,
  },
  {
    key: 'percentNotHome',
    label: '% Not Home',
    kpiOnly: true,
    format: formatPercent,
  },
  {
    key: 'percentCallBack',
    label: '% Call Back',
    kpiOnly: true,
    format: formatPercent,
  },
  { key: 'knocksToSales', label: 'Knocks Vs. Sales', kpiOnly: true },
  {
    key: 'notInterestedToSales',
    label: 'Not Interested Vs. Sales',
    kpiOnly: true,
  },
  { key: 'gavePitchToSales', label: 'Gave Pitch Vs. Sales', kpiOnly: true },
];

// ─── Component ───────────────────────────────────────────────────────────────

export function ActivityPage() {
  // @ts-ignore
  const user = global.me;
  const isAdmin = user?.isAdmin;
  const isManager = !isAdmin && user?.functionalRoles?.includes('manager');

  // Redirect non-admin/non-manager users
  React.useEffect(() => {
    if (!user || (!isAdmin && !isManager)) {
      window.history.back();
    }
  }, [user, isAdmin, isManager]);

  const hasMultipleManagerCircles =
    isManager && user?.myManagerCircles && user.myManagerCircles.length > 1;

  const defaultTeamId = React.useMemo(() => {
    if (
      isManager &&
      user?.myManagerCircles &&
      user.myManagerCircles.length === 1
    ) {
      return user.myManagerCircles[0];
    }
    return 'all';
  }, [isManager, user]);

  const allowAllTeamsOption = isAdmin || hasMultipleManagerCircles;

  // State
  const [loading, setLoading] = React.useState(true);
  const [activityData, setActivityData] = React.useState<ActivityItem[]>([]);
  const [error, setError] = React.useState<string | null>(null);
  const [page, setPage] = React.useState(0);
  const rowsPerPage = 25;

  const [selectedTeamId, setSelectedTeamId] =
    React.useState<string>(defaultTeamId);
  const [selectedDate, setSelectedDate] =
    React.useState<string>(getCurrentLocalDate);
  const [dateError, setDateError] = React.useState<string | null>(null);
  const [updatePickerKey, setUpdatePickerKey] = React.useState(0);

  const [order, setOrder] = React.useState<Order>('desc');
  const [orderBy, setOrderBy] = React.useState<SortableKey>('pinsDropped');

  const [userActivityKpis, setUserActivityKpis] = React.useState<
    UserActivityKpis | undefined
  >(getUserActivityKpis());
  const [kpisLoaded, setKpisLoaded] = React.useState(false);

  // Load KPIs on mount
  React.useEffect(() => {
    const kpis = getUserActivityKpis();
    if (kpis) {
      setUserActivityKpis(kpis);
    }
    setKpisLoaded(true);
  }, []);

  const hasKpis =
    userActivityKpis !== undefined && Object.keys(userActivityKpis).length > 0;

  const columns = React.useMemo(() => {
    if (hasKpis) {
      return [...BASE_COLUMNS, ...KPI_COLUMNS];
    }
    return BASE_COLUMNS;
  }, [hasKpis]);

  // ─── Data fetching ───────────────────────────────────────────────────────

  const fetchActivityData = React.useCallback(
    async (teamId?: string, date?: string) => {
      let targetTeamId = teamId || defaultTeamId;

      if (isManager && targetTeamId === 'all' && !hasMultipleManagerCircles) {
        targetTeamId = user.myManagerCircles[0];
      }

      const targetDate = date || getCurrentLocalDate();
      setPage(0);
      setLoading(true);
      setError(null);

      const freshKpis = getUserActivityKpis();
      if (freshKpis) {
        setUserActivityKpis(freshKpis);
      }

      try {
        const currentUser = await getCurrentUser();
        if (!currentUser) {
          throw new Error('You are not authenticated. Please sign in again.');
        }

        const url = 'https://api.gogamify.com/activity/team';
        const token = await getBearerToken();

        if (!token) {
          throw new Error(
            'Authentication token is missing. Please sign in again.'
          );
        }

        const userRole = isAdmin ? 'admin' : 'manager';
        const requestBody: Record<string, any> = {
          team_id: targetTeamId,
          user_id: user?.id || null,
          date: targetDate,
          org_id: user?.orgID || null,
          user_role: userRole,
        };

        if (userActivityKpis && Object.keys(userActivityKpis).length > 0) {
          requestBody.activity_kpis = userActivityKpis;
        }

        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 15000);

        const httpResponse = await fetch(url, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify(requestBody),
          signal: controller.signal,
        });

        clearTimeout(timeoutId);

        if (!httpResponse.ok) {
          let errorDetails = '';
          try {
            const errorResponse = await httpResponse.json();
            errorDetails = JSON.stringify(errorResponse);
          } catch {
            try {
              errorDetails = await httpResponse.text();
            } catch {
              // ignore
            }
          }
          throw new Error(
            `Error ${httpResponse.status}: ${
              httpResponse.statusText || 'Data is not available'
            }. Details: ${errorDetails}`
          );
        }

        const response = await httpResponse.json();
        const displayTeam =
          targetTeamId === 'all' ? 'All Teams' : response.team_name;

        const transformedData: ActivityItem[] = response.team_metrics.map(
          (metric: ApiTeamMetric, index: number) => ({
            id: `${targetTeamId}-${targetDate}-${index}`,
            name: metric.name || '--',
            team: displayTeam,
            firstContactAfter9AM: metric.first_pin_after_9am,
            thirdContactAfter9AM: metric.third_pin_after_9am,
            lastBefore3pm: metric.last_before_3pm,
            firstAfter4pm: metric.first_after_4pm,
            thirdToLastPin: metric.third_to_last_pin,
            lastPin: metric.last_pin,
            pinsDropped: formatDisplayValue(metric.pins_dropped),
            sales: formatDisplayValue(metric.sales),
            notHomeCount: formatDisplayValue(metric.not_home_count),
            notInterestedCount: formatDisplayValue(metric.not_interested_count),
            gavePitchCount: formatDisplayValue(metric.gave_pitch_count),
            callBacksCount: formatDisplayValue(metric.call_backs_count),
            percentNotInterested: formatDisplayValue(
              metric.percent_not_interested
            ),
            percentGavePitch: formatDisplayValue(metric.percent_gave_pitch),
            percentNotHome: formatDisplayValue(metric.percent_not_home),
            percentCallBack: formatDisplayValue(metric.percent_call_back),
            knocksToSales: formatDisplayValue(metric.knocks_to_sales),
            notInterestedToSales: formatDisplayValue(
              metric.not_interested_to_sales
            ),
            gavePitchToSales: formatDisplayValue(metric.gave_pitch_to_sales),
          })
        );

        if (transformedData.length === 0) {
          setError(
            'There is no data for the selected date or team. Try changing your filters.'
          );
          setActivityData([]);
        } else {
          setActivityData(transformedData);
          setError(null);
        }
        setLoading(false);
      } catch (err: any) {
        setError(
          err.message || 'An unexpected error occurred. Please try again.'
        );
        setActivityData([]);
        setLoading(false);
      }
    },
    [
      defaultTeamId,
      isAdmin,
      isManager,
      hasMultipleManagerCircles,
      user,
      userActivityKpis,
    ]
  );

  // Initial fetch once KPIs are loaded
  React.useEffect(() => {
    if (kpisLoaded && user?.orgID) {
      fetchActivityData(defaultTeamId, getCurrentLocalDate());
    }
  }, [kpisLoaded, user?.orgID, defaultTeamId, fetchActivityData]);

  // ─── Handlers ──────────────────────────────────────────────────────────────

  const handleTeamSelect = React.useCallback(
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    (id: string, _name: string) => {
      const effectiveId = id === 'all' ? 'all' : id;
      setSelectedTeamId(effectiveId);

      if (!dateError) {
        fetchActivityData(effectiveId, selectedDate);
      }
      setUpdatePickerKey((prev) => prev + 1);
    },
    [dateError, selectedDate, fetchActivityData]
  );

  const handleDateChange = React.useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const newDate = e.target.value;
      setSelectedDate(newDate);

      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const selectedDateObj = new Date(newDate);

      if (selectedDateObj > today) {
        setDateError(
          "You've selected a date in the future. Please select today or a past date."
        );
      } else {
        setDateError(null);
        fetchActivityData(selectedTeamId, newDate);
      }
    },
    [selectedTeamId, fetchActivityData]
  );

  const handleRequestSort = React.useCallback(
    (property: SortableKey) => {
      const isAsc = orderBy === property && order === 'asc';
      setOrder(isAsc ? 'desc' : 'asc');
      setOrderBy(property);
    },
    [order, orderBy]
  );

  const handleRefresh = React.useCallback(() => {
    fetchActivityData(selectedTeamId, selectedDate);
  }, [fetchActivityData, selectedTeamId, selectedDate]);

  // ─── Derived data ──────────────────────────────────────────────────────────

  const sortedData = React.useMemo(
    () => sortData(activityData, orderBy, order),
    [activityData, orderBy, order]
  );

  const paginatedData = React.useMemo(() => {
    const start = page * rowsPerPage;
    return sortedData.slice(start, start + rowsPerPage);
  }, [sortedData, page, rowsPerPage]);

  const totalPages = Math.ceil(sortedData.length / rowsPerPage);

  // ─── Sort icon helper ──────────────────────────────────────────────────────

  const SortIcon = React.useCallback(
    ({ columnKey }: { columnKey: SortableKey }) => {
      if (orderBy !== columnKey) {
        return <ArrowUpDown className="ml-1 size-3 text-muted-foreground/50" />;
      }
      if (order === 'asc') {
        return <ArrowUp className="ml-1 size-3" />;
      }
      return <ArrowDown className="ml-1 size-3" />;
    },
    [orderBy, order]
  );

  // ─── Render ────────────────────────────────────────────────────────────────

  const getCellValue = (row: ActivityItem, col: ColumnDef): string => {
    const raw = row[col.key];
    if (raw === null || raw === undefined) {
      return '--';
    }
    if (col.format) {
      return col.format(raw);
    }
    return String(raw);
  };

  return (
    <div className="h-full w-full flex flex-col overflow-hidden">
      <PageHeader
        title="Team/User Activity"
        avatar={{
          src: global.me?.imageName || undefined,
          name: global.me?.name,
        }}
      />

      <div className="flex-1 overflow-auto px-6 py-4 space-y-4 min-w-0">
        {/* Filters */}
        <div className="grid grid-cols-2 sm:flex sm:flex-wrap sm:items-end gap-3 sm:gap-4 p-4 rounded-lg border border-border bg-card">
          {/* Team Filter */}
          <div className="flex flex-col gap-1.5">
            <label className="text-sm font-medium text-foreground">Team</label>
            <Formik
              initialValues={{ teamFilter: selectedTeamId }}
              onSubmit={() => {}}
              enableReinitialize
            >
              {() => (
                <div className="v2-team-picker-wrapper">
                  <style>{`
                    .v2-team-picker-wrapper select.field-dropdown {
                      border-radius: 0.375rem !important;
                      max-width: none !important;
                      height: 36px !important;
                      padding-right: 40px !important;
                      padding-left: 12px !important;
                      background-color: hsl(var(--background)) !important;
                      color: hsl(var(--foreground)) !important;
                      border: 1px solid hsl(var(--border)) !important;
                      font-family: inherit !important;
                      font-size: 14px !important;
                    }
                  `}</style>
                  <NewSelectCircleOrPerson
                    key={`team-picker-${updatePickerKey}`}
                    name="teamFilter"
                    value={selectedTeamId}
                    fieldSetter={handleTeamSelect}
                    showAll={true}
                    allOption={allowAllTeamsOption}
                    noAutoSelect={true}
                    pickCircles={true}
                    pickUsers={false}
                    useCirclesList={false}
                    useManagerCirclesList={isManager}
                    update={updatePickerKey}
                    style={{
                      height: '36px',
                      width: '100%',
                      padding: '8px 40px 8px 12px',
                      appearance: 'none',
                      WebkitAppearance: 'none',
                      backgroundImage: 'url("/images/select-dropdown.svg")',
                      backgroundSize: '14px',
                      backgroundRepeat: 'no-repeat',
                      backgroundPosition: 'calc(100% - 14px) center',
                      fontSize: '14px',
                    }}
                  />
                </div>
              )}
            </Formik>
          </div>

          {/* Date Filter */}
          <div className="flex flex-col gap-1.5">
            <label className="text-sm font-medium text-foreground">Date</label>
            <input
              type="date"
              value={selectedDate}
              onChange={handleDateChange}
              className="flex h-9 w-full sm:w-[200px] rounded-lg border border-input bg-background px-3 py-2 text-sm text-foreground shadow-sm ring-offset-background focus:outline-none focus:ring-1 focus:ring-ring disabled:cursor-not-allowed disabled:opacity-50 dark:[color-scheme:dark]"
            />
            {dateError && (
              <p className="text-xs text-destructive">{dateError}</p>
            )}
          </div>

          {/* Refresh Button */}
          <div className="col-span-2 sm:col-span-1 sm:ml-auto">
            <Button
              onClick={handleRefresh}
              disabled={loading}
              className="gap-2 w-full sm:w-auto"
            >
              {loading ? (
                <Loader2 className="size-4 animate-spin" />
              ) : (
                <RefreshCw className="size-4" />
              )}
              {loading ? 'Refreshing...' : 'Refresh Data'}
            </Button>
          </div>
        </div>

        {/* Content */}
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="size-8 animate-spin text-muted-foreground" />
          </div>
        ) : error ? (
          <div className="flex items-center justify-center py-20 rounded-lg border border-border bg-card">
            <p className="text-muted-foreground text-center">{error}</p>
          </div>
        ) : (
          <>
            {/* Table */}
            <div className="rounded-lg border border-border overflow-hidden">
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-primary hover:bg-primary">
                      {columns.map((col) => (
                        <TableHead
                          key={col.key}
                          className={cn(
                            'text-primary-foreground font-bold whitespace-nowrap cursor-pointer select-none hover:bg-primary/90 transition-colors',
                            col.key === 'name' &&
                              'sticky left-0 z-10 bg-primary'
                          )}
                          onClick={() => handleRequestSort(col.key)}
                        >
                          <span className="inline-flex items-center">
                            {col.label}
                            <SortIcon columnKey={col.key} />
                          </span>
                        </TableHead>
                      ))}
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {paginatedData.length > 0 ? (
                      paginatedData.map((row) => (
                        <TableRow key={row.id}>
                          {columns.map((col) => (
                            <TableCell
                              key={col.key}
                              className={cn(
                                'whitespace-nowrap',
                                col.key === 'name' &&
                                  'sticky left-0 z-[1] bg-background font-semibold shadow-[2px_0_3px_rgba(0,0,0,0.05)]'
                              )}
                            >
                              {getCellValue(row, col)}
                            </TableCell>
                          ))}
                        </TableRow>
                      ))
                    ) : (
                      <TableRow>
                        <TableCell
                          colSpan={columns.length}
                          className="text-center text-muted-foreground py-10"
                        >
                          No data available
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </div>
            </div>

            {/* Pagination */}
            {sortedData.length > rowsPerPage && (
              <div className="flex items-center justify-between px-2 py-2">
                <p className="text-sm text-muted-foreground">
                  Showing {page * rowsPerPage + 1} -{' '}
                  {Math.min((page + 1) * rowsPerPage, sortedData.length)} of{' '}
                  {sortedData.length}
                </p>
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setPage((p) => Math.max(0, p - 1))}
                    disabled={page === 0}
                  >
                    Previous
                  </Button>
                  <span className="text-sm text-muted-foreground">
                    Page {page + 1} of {totalPages}
                  </span>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() =>
                      setPage((p) => Math.min(totalPages - 1, p + 1))
                    }
                    disabled={page >= totalPages - 1}
                  >
                    Next
                  </Button>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
